import React, { useState, useCallback } from 'react';
import { useQueryAndAnswerMutation } from '@common-reducers/backend-api/backend-api';
import { KnowledgeBaseDocument } from '@common/models/knowledge-base-document';


// Shadcn UI Components
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { useAppSelector } from '@/common/reducers/hooks/store.hook';
import { selectUser } from '@/common/reducers/UserSelectors';

interface TestQueryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  document: KnowledgeBaseDocument;
}

// Define type for the reranked response structure if possible
// based on what queryKnowledgeBasesWithReranking returns
interface RerankedResponseData {
  results: any[]; // Replace 'any' with a specific RerankedChunk type if available
  debug?: { 
    totalResults?: number;
    threshold?: number;
    // other debug fields...
  };
  // other fields...
}

export const TestQueryDialog: React.FC<TestQueryDialogProps> = ({
  open,
  onOpenChange,
  document
}) => {
  // State
  const [queryText, setQueryText] = useState('');
  const [generatedAnswer, setGeneratedAnswer] = useState<string | null>(null); // State for LLM answer
  const [rerankedResult, setRerankedResult] = useState<RerankedResponseData | null>(null); // State for detailed results
  
  // API hooks
  const [queryAndAnswer, { isLoading: isQueryLoading }] = useQueryAndAnswerMutation();

  // Get user role
  const user = useAppSelector(selectUser);
  const isAdmin = user?.role === 'admin' || user?.role === 'support';

  const handleRunQuery = useCallback(async () => {
    if (!document || !queryText.trim()) return;
    
    setGeneratedAnswer(null); // Reset states
    setRerankedResult(null);
    try {
      // Call the new action
      const response = await queryAndAnswer({
        knowledgeBaseIds: [document.knowledgeBaseId],
        documentIds: [document._id],
        query: queryText.trim(),
        limit: 5, // Keep limit reasonable for context generation
        similarityThreshold: 0.3 // Initial threshold for retrieval
      }).unwrap();
      
      // Store the generated answer and the detailed reranked response
      setGeneratedAnswer(response.generatedAnswer);
      setRerankedResult(response.rerankedResponse); 
    } catch (error: any) {
      console.error('Failed to run query and get answer:', error);
      toast.error("Query failed", {
        description: error.message || 'Failed to process query. Please try again.',
      });
      setGeneratedAnswer(null);
      setRerankedResult(null);
    }
  }, [document, queryText, queryAndAnswer]);

  return (
    <Dialog 
      open={open} 
      onOpenChange={onOpenChange}
    >
      <DialogContent 
        className="max-w-7xl w-full"
        style={{
          maxHeight: '80vh',
          overflowY: 'auto',
        }}
      >
        <DialogHeader>
          <DialogTitle>Test Query</DialogTitle>
          <DialogDescription>
            Test a query against the document: {document?.name}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 my-4 w-full">
          <div>
            <label htmlFor="query-input" className="text-sm font-medium text-gray-700 mb-1 block">
              Enter your query
            </label>
            <Textarea
              id="query-input"
              placeholder="Ask a question about the document..."
              value={queryText}
              onChange={(e) => setQueryText(e.target.value)}
              className="min-h-[80px] w-full"
              disabled={isQueryLoading}
              dir="auto"
            />
          </div>
          
          {isQueryLoading && (
            <div className="py-2">
              <Progress value={undefined} className="h-2" />
              <p className="text-center text-sm text-gray-600 mt-2">
                Processing query...
              </p>
            </div>
          )}
          
          {generatedAnswer && !isQueryLoading && (
            <div className="mb-6 p-4 border border-emerald-200 bg-emerald-50 rounded-md shadow-sm w-full">
                <h4 className="text-lg font-semibold text-emerald-800 mb-2">Generated Answer</h4>
                <p className="text-emerald-900 whitespace-pre-wrap" dir="auto">{generatedAnswer}</p>
            </div>
          )}

          {/* Conditionally render Retrieved Chunks for Admins/Support */}
          {isAdmin && rerankedResult && rerankedResult.results && rerankedResult.results.length > 0 && !isQueryLoading && (
            <div className="w-full">
              <h4 className="text-lg font-semibold text-gray-700 mb-3 border-t pt-4">Retrieved Chunks (Reranked) [Admin View]</h4>
              <div className="space-y-4 max-h-[400px] overflow-y-auto pr-2 custom-scrollbar w-full">
                 {/* Debug Info (Optional) */}
                 {/* {rerankedResult.debug && (
                    <div className="bg-blue-50 p-3 rounded-md text-sm mb-3">
                       <p className="font-medium text-blue-700">Debug Info</p>
                       <pre className="text-xs whitespace-pre-wrap">{JSON.stringify(rerankedResult.debug, null, 2)}</pre>
                    </div>
                 )} */} 
                
                 {rerankedResult.results.map((result: any, index: number) => (
                  <div key={index} className="border border-gray-200 rounded-md overflow-hidden bg-white shadow-sm w-full">
                    <div className="bg-gray-50 p-3 flex justify-between items-center border-b border-gray-200">
                      <div className="font-medium">Chunk #{index + 1}</div>
                      <div className="text-sm space-x-3">
                         {/* Display Reranker Score if available */} 
                         {result.relevanceScore !== undefined && (
                            <span className="bg-blue-100 text-blue-800 px-2 py-0.5 rounded text-xs">
                               <span className="font-medium">Relevance:</span> 
                               {/* Adjust scaling: Assume score is 0-10, multiply by 10 for % */}
                               <span className={`ml-1 font-semibold ${result.relevanceScore > 7 ? 'text-emerald-700' : result.relevanceScore > 4 ? 'text-amber-700' : 'text-red-700'}`}>
                                   {(result.relevanceScore * 10)?.toFixed(1)}% 
                               </span>
                            </span>
                         )}
                         {/* Display Original Similarity if needed */} 
                          {result.similarity !== undefined && (
                            <span className="bg-gray-100 text-gray-700 px-2 py-0.5 rounded text-xs">
                               <span className="font-medium">Similarity:</span> 
                               {/* Similarity is likely 0-1, multiply by 100 */}
                               <span className="ml-1">
                                   {(result.similarity * 100)?.toFixed(1)}%
                               </span>
                            </span>
                          )}
                      </div>
                    </div>
                    <div className="p-4">
                      <div className="text-xs text-gray-500 mb-2">
                        {/* Metadata Display */}
                        <span className="font-medium">Source:</span> {result.metadata?.documentName || 'N/A'} 
                        {result.metadata?.chunkIndex !== undefined && (
                          <span className="ml-1 text-gray-400">(Chunk {result.metadata.chunkIndex})</span>
                        )}
                        {result.metadata?.startOffset !== undefined && result.metadata?.endOffset !== undefined && (
                           <span className="ml-2 text-gray-400">[{result.metadata.startOffset}-{result.metadata.endOffset}]</span>
                        )}
                      </div>
                      {/* Display Reranker Reasoning if available */} 
                      {result.rerankerReasoning && (
                        <div className="text-xs italic text-purple-800 bg-purple-50 p-2 rounded mb-2 border border-purple-100" dir="auto">
                           <strong>Reasoning:</strong> {result.rerankerReasoning}
                        </div>
                      )}
                      {/* Display Key Insights if available */} 
                      {result.keyInsights && result.keyInsights.length > 0 && (
                         <div className="text-xs text-indigo-800 bg-indigo-50 p-2 rounded mb-2 border border-indigo-100" dir="auto">
                             <strong>Insights:</strong> {result.keyInsights.join(', ')}
                         </div>
                      )}
                      {/* Chunk Content */} 
                      <div dir="auto" className="font-mono text-xs p-3 border border-gray-200 rounded bg-gray-50 mt-2" style={{ 
                          maxHeight: '150px', 
                          overflowY: 'auto',
                          maxWidth: '100%', 
                          wordBreak: 'break-word',
                          whiteSpace: 'pre-wrap'
                      }}>
                        {result.content}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Handle case where query ran but returned no chunks (show this message to all users) */}
          {rerankedResult && (!rerankedResult.results || rerankedResult.results.length === 0) && !isQueryLoading && ( 
             <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4 text-center text-yellow-800 text-sm mt-4">
                {/* Slightly different message based on role */} 
                {isAdmin 
                  ? "No relevant chunks were found in the document for this query."
                  : "I couldn't find specific text segments in the document to base the answer on, but here is the generated response."} 
             </div>
          )} 

        </div>
        <DialogFooter className="mt-6 pt-4 border-t">
          <Button 
            variant="outline" 
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleRunQuery}
            disabled={!queryText.trim() || isQueryLoading}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
          >
            {isQueryLoading ? 'Processing...' : 'Run Query'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TestQueryDialog; 